function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react';
import { eventWithTime } from '@rrweb/types';
import { SelectReact } from 'common/components/select';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { Directions } from 'common/components/drop-menu/types';
import { useTranslation } from 'common/i18n/hooks';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { reporter } from 'common/report/error-reporting';
import { isTestEnv } from 'common/report/util/env';
import { SwitchReact } from 'common/components/switch';
import { useThemes } from 'common/contexts/react/themes-context';
import { TooltipReact } from 'common/components/tooltip';
import { useLocalStorageManager } from 'common/contexts/react/storage-manager-context';
import { SkeletonListReact } from 'common/components/skeleton-list';
import { SkeletonListRowSize } from 'common/components/skeleton-list/types';
import { TranslationKey } from 'common/i18n';
import { CopyToClipboardButtonReact } from '../copy-to-clipboard/copy-to-clipboard-button';
import { CopyType } from '../copy-to-clipboard/copy-to-clipboard-button/types';
import styles from './index.module.styl';
import { EmbedErrorStateType, EmbedReplayPayload, ParentAppReplayMessage, RecordingEventMetadata, RecordingInactivePeriod, RecordingMetadata, RecordingPageViewEventMetadata, RecordingSpeed, ReplayErrorStateType, WindowMessageRecipient } from './types';
import { fetchSignedReplayParams, formatRecordingProgressTimeBar, sendEmbedReplayMessage, useReplayWindowMessageListener } from './util';
import { RecordingPlayerTimeline } from './timeline';
import { SessionRecordingErrorState } from './error-state';
export const PLAYER_URL = REPLAY_FROM_SAME_ORIGIN ? "/replay-embed" : window["__MP_REPLAY_EMBED_URL"];
type RecordingPlayerProps = {
  children?: React.ReactNode;
  copyButton?: React.ReactNode;
  currentRecordingEventSelectedTime: number;
  currentRecordingPaused: boolean;
  isFullscreen: boolean;
  isSkipInactivityEnabled: boolean;
  playlistCollapsed: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  showSkipInactiveSwitch: boolean;
  searchFilter: string;
  replayPageViewEventsLoaded: boolean;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
  hasReplayPageViewEvents: boolean;
  isStitchingEnabled: boolean;
  errorState?: EmbedErrorStateType | ReplayErrorStateType;
  isLoading?: boolean;
  currentHoveredEvent: RecordingEventMetadata | null;
  setCurrentHoveredEvent: (event: RecordingEventMetadata | null) => void;
  onFinishReplay: (replayId: string) => void;
  onHalfwayThrough: (replayId: string) => void;
  onStartReplay: (replayId: string) => void;
  onToggleFullscreen: () => void;
  onToggleSkipInactivity: (isSkipInactivityEnabled: boolean) => void;
  onToggleCurrentRecordingPaused: (isPaused: boolean) => void;
  updateCurrentReplayEvent: (eventTime: number) => void;
  onTrackReplayLoaded: (props: Obj) => void;
};
function ErrorState(props: {
  type: EmbedErrorStateType | ReplayErrorStateType;
}) {
  let titleKey: TranslationKey;
  let subtitleKey: TranslationKey;
  if (props.type === EmbedErrorStateType.EmbedBlocked) {
    titleKey = "session-replay.failed-to-load-player";
    subtitleKey = "session-replay.failed-to-load-player-error";
  } else if (props.type === ReplayErrorStateType.NoReplaysAvailable) {
    titleKey = "session-replay.no-replays-available";
    subtitleKey = "session-replay.no-replays-available";
  } else {
    titleKey = "session-replay.no-replay-found";
    subtitleKey = "session-replay.no-replay-found-error";
  }
  return <SessionRecordingErrorState titleKey={titleKey} subtitleKey={subtitleKey} subtitleLink={"https://docs.mixpanel.com/docs/session-replay/session-replay-web#faq"} data-sentry-element="SessionRecordingErrorState" data-sentry-component="ErrorState" data-sentry-source-file="player.tsx" />;
}

// How long before the event time to jump to when the user clicks on an event in seconds
export const EVENT_TIME_JUMP_OFFSET = 2;
// How long before the event time to jump to when the user views replays from insights in seconds
export const REPORT_EVENT_TIME_JUMP_OFFSET = 7;
const RECORDING_SPEED_STORAGE_KEY = "recordingPlayerSpeed";
export function RecordingPlayer(props: RecordingPlayerProps) {
  var _props$replay2, _props$replay4;
  const [loadingRecording, setLoadingRecording] = useState<boolean>(true);
  const [errorState, setErrorState] = useState<EmbedErrorStateType | null>(null);
  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [recordingFinished, setRecordingFinished] = useState<boolean>(false);
  const [recordingReachedHalf, setRecordingReachedHalf] = useState<boolean>(false);
  const [recordingSpeed, setRecordingSpeed] = useState<RecordingSpeed>(RecordingSpeed.Normal);
  const [lastUserRecordingSpeed, setLastUserRecordingSpeed] = useState<RecordingSpeed>(RecordingSpeed.Normal);
  const [isCurrentPeriodInactive, setIsCurrentPeriodInactive] = useState<boolean>(false);
  const [replayInactivePeriods, setReplayInactivePeriods] = useState<RecordingInactivePeriod[]>([]);
  const [pageloadEvents, setPageloadEvents] = useState<eventWithTime[]>([]);
  const [embedReady, setEmbedReady] = useState(false);
  const [currentURL, setCurrentURL] = useState<string>("");
  const embedIframeRef = useRef<HTMLIFrameElement>(null);
  const currentProject = useCurrentProject();
  const {
    t
  } = useTranslation();
  const themesResource = useThemes();
  const localStorage = useLocalStorageManager();
  const hasError = Boolean(props.errorState || errorState);
  useEffect(() => {
    var _props$replay;
    if ((_props$replay = props.replay) !== null && _props$replay !== void 0 && _props$replay.replayId) {
      sendReplayParams(props.replay);
    }
  }, [(_props$replay2 = props.replay) === null || _props$replay2 === void 0 ? void 0 : _props$replay2.replayId, embedReady]);
  useEffect(() => {
    if (props.currentRecordingEventSelectedTime && embedIframeRef.current && recordingDuration) {
      goToEventTime();
    }
  }, [props.currentRecordingEventSelectedTime, embedIframeRef.current, recordingDuration]);
  useEffect(() => {
    if (props.isSkipInactivityEnabled && isCurrentPeriodInactive) {
      setIsCurrentPeriodInactive(false);
      updatePlayerSpeed(lastUserRecordingSpeed, true);
    }
  }, [props.isSkipInactivityEnabled]);
  useEffect(() => {
    if (embedIframeRef.current) {
      if (recordingFinished) {
        setRecordingFinished(false);
        setRecordingProgress(0);
        sendEmbedMessage({
          recordingPaused: false
        });
      } else {
        sendEmbedMessage({
          recordingPaused: props.currentRecordingPaused
        });
      }
    }
  }, [props.currentRecordingPaused]);
  useEffect(() => {
    setRecordingSpeed(getSavedReplaySpeed());
  }, []);
  useReplayWindowMessageListener<ParentAppReplayMessage>(WindowMessageRecipient.ParentApp, window, message => {
    const payload = message.payload;
    if (payload.embedReady) {
      setEmbedReady(true);
    }
    if (payload.embedBlocked) {
      setErrorState(EmbedErrorStateType.EmbedBlocked);
    }
    if (payload.recordingProgress) {
      setRecordingProgress(payload.recordingProgress);
      props.updateCurrentReplayEvent(payload.recordingProgress / 1000 + props.replay.time);
      updateSkippingInactiveStatus();
      findCurrentURL(payload.recordingProgress);
    }
    if (payload.recordingDuration) {
      setRecordingDuration(payload.recordingDuration);
    }
    if ("loadingRecording" in payload) {
      setLoadingRecording(payload.loadingRecording);
    }
    if (recordingDuration > 0 && !recordingReachedHalf && !props.currentRecordingPaused && payload.recordingProgress / recordingDuration >= 0.5) {
      setRecordingReachedHalf(true);
      props.onHalfwayThrough(props.replay.replayId);
    }
    if (payload.recordingStarted) {
      setRecordingFinished(false);
      setRecordingReachedHalf(false);
      props.onToggleCurrentRecordingPaused(false);
      setLoadingRecording(false);
    }
    if (payload.recordingFinished) {
      setRecordingFinished(true);
      props.onFinishReplay(props.replay.replayId);
    }
    if (payload.recordingInactivePeriods) {
      setReplayInactivePeriods(payload.recordingInactivePeriods);
    }
    if (payload.pageloadEvents) {
      setPageloadEvents(payload.pageloadEvents);
    }
    if (payload.error) {
      reporter.error(payload.error);
      setErrorState(EmbedErrorStateType.NoReplayFound);
    }
    if (payload.playerClicked) {
      toggleRecording();
    }
    if (payload.trackReplayLoaded) {
      props.onTrackReplayLoaded(payload.trackReplayLoaded);
    }
  });
  async function sendReplayParams(replay: RecordingMetadata) {
    setLoadingRecording(true);
    const signedReplayParams = await fetchSignedReplayParams(replay.replayId, replay.replayEnv);
    const loadReplayParams = {
      signedReplayParams,
      targetOrigin: window.location.origin,
      retentionPeriod: replay.retentionPeriod
    };
    sendEmbedMessage({
      loadReplayParams
    });
    setRecordingFinished(false);
    props.onStartReplay(replay.replayId);
    setLoadingRecording(false);
  }
  function updateSkippingInactiveStatus() {
    const currentTime = props.replay.time * 1000 + recordingProgress;
    const nextInactivePeriod = replayInactivePeriods.find(period => {
      return currentTime >= period.startTimeMs && currentTime <= period.endTimeMs;
    });
    if (props.isSkipInactivityEnabled && nextInactivePeriod) {
      const skipToTime = nextInactivePeriod.endTimeMs - props.replay.time * 1000;
      sendEmbedMessage({
        recordingProgress: skipToTime
      });
      setIsCurrentPeriodInactive(false);
    } else if (Boolean(nextInactivePeriod) !== isCurrentPeriodInactive) {
      setIsCurrentPeriodInactive(Boolean(nextInactivePeriod));
      if (nextInactivePeriod) {
        updatePlayerSpeed(RecordingSpeed.Dectuple, true);
      } else {
        updatePlayerSpeed(lastUserRecordingSpeed, true);
      }
    }
  }
  function findCurrentURL(progress: number) {
    var _props$replay3;
    let newCurrentURL = ((_props$replay3 = props.replay) === null || _props$replay3 === void 0 ? void 0 : _props$replay3.startingURL) || t("session-replay.unknown-starting-url");
    if (props.replay && props.replayPageViewEvents) {
      [...props.replayPageViewEvents].sort((a, b) => a.eventTime - b.eventTime).forEach(event => {
        if (event.eventTime <= props.replay.time + progress / 1000) {
          newCurrentURL = event.currentURL;
        }
      });
    }
    setCurrentURL(newCurrentURL);
  }
  function getSavedReplaySpeed(): RecordingSpeed {
    const savedSpeed = localStorage.getItem(RECORDING_SPEED_STORAGE_KEY);
    if (savedSpeed) {
      return Number(savedSpeed) as RecordingSpeed;
    }
    return RecordingSpeed.Normal;
  }
  function saveReplaySpeed(speed: RecordingSpeed) {
    localStorage.setItem(RECORDING_SPEED_STORAGE_KEY, speed.toString());
  }
  function sendEmbedMessage(payload: EmbedReplayPayload) {
    if (embedIframeRef.current) {
      sendEmbedReplayMessage(_objectSpread(_objectSpread({}, payload), {}, {
        isDarkMode: themesResource.isDarkMode()
      }), embedIframeRef.current.contentWindow);
    }
  }
  function goToEventTime() {
    if (props.currentRecordingEventSelectedTime && embedIframeRef.current && recordingDuration) {
      const targetEventTime = props.currentRecordingEventSelectedTime - EVENT_TIME_JUMP_OFFSET;
      const timeFromRecordingStart = Math.max(targetEventTime - props.replay.time, 0);
      sendEmbedMessage({
        recordingProgress: timeFromRecordingStart * 1000,
        recordingPaused: props.currentRecordingPaused
      });
      if (timeFromRecordingStart * 1000 < recordingDuration) {
        setRecordingFinished(false);
      }
      findCurrentURL(timeFromRecordingStart * 1000);
    }
  }
  function toggleRecording() {
    if (embedIframeRef.current) {
      if (recordingFinished) {
        setRecordingFinished(false);
        props.onToggleCurrentRecordingPaused(false);
        setRecordingProgress(0);
        sendEmbedMessage({
          recordingPaused: false
        });
      } else {
        props.onToggleCurrentRecordingPaused(!props.currentRecordingPaused);
        sendEmbedMessage({
          recordingPaused: !props.currentRecordingPaused
        });
      }
    }
  }
  function updatePlayerSpeed(newSpeed: RecordingSpeed) {
    let inactivityChange = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (embedIframeRef.current) {
      setRecordingSpeed(newSpeed);
      sendEmbedMessage({
        recordingSpeed: newSpeed
      });
      if (!inactivityChange) {
        setLastUserRecordingSpeed(newSpeed);
        saveReplaySpeed(newSpeed);
      }
    }
  }
  const formatRecordingProgressTimeBarResult = formatRecordingProgressTimeBar(recordingProgress, recordingDuration);
  const currentURLValue = currentURL || t("session-replay.unknown-starting-url");
  return <div className={styles.recordingContent} data-sentry-component="RecordingPlayer" data-sentry-source-file="player.tsx">
      {!hasError && ((_props$replay4 = props.replay) === null || _props$replay4 === void 0 ? void 0 : _props$replay4.source) === "web" && <div className={styles.recordingUrlDisplay}>
          <CopyToClipboardButtonReact className={styles.recordingUrlDisplayIcon} copyType={CopyType.Link} options={[{
        value: currentURLValue
      }]} theme={ButtonTheme.Tertiary} disabled={!props.replayPageViewEventsLoaded} />
          {!props.replayPageViewEventsLoaded || !props.replay ? <SkeletonListReact className={styles.recordingUrlDisplaySkeleton} numRows={1} rowSize={SkeletonListRowSize.Large} /> : <div className={styles.recordingUrlDisplayText}>
              {currentURLValue}
              <TooltipReact text={currentURLValue} />
            </div>}
          {!props.hasReplayPageViewEvents && <TooltipReact text={t("session-replay.no-page-view-events")} />}
        </div>}
      <div className={styles.playerIframeContainer} role={"region"} style={loadingRecording || hasError ? {
      visibility: "hidden"
    } : {}} onError={error => {
      var _currentProject$get$v, _currentProject$get$v2;
      if (((_currentProject$get$v = currentProject.get().value) === null || _currentProject$get$v === void 0 ? void 0 : _currentProject$get$v.id) === 3 || ((_currentProject$get$v2 = currentProject.get().value) === null || _currentProject$get$v2 === void 0 ? void 0 : _currentProject$get$v2.id) === 132990) {
        error.preventDefault();
      }
    }} onClick={() => toggleRecording()}>
        {/* interactions with the actual player must be tested separately */}
        {!isTestEnv() && <iframe className={styles.playerIframe} src={PLAYER_URL} ref={embedIframeRef} />}
      </div>
      <div className={styles.playerControls}>
        {embedIframeRef.current && !hasError && <div className={styles.playerControlsSpace}>
            <div className={styles.playerControlsProgressBarArea}>
              <RecordingPlayerTimeline replay={props.replay} replayEvents={props.replayEvents} recordingDuration={recordingDuration} recordingProgress={recordingProgress} recordingSkippingInactive={isCurrentPeriodInactive} replayInactivePeriods={replayInactivePeriods} pageloadEvents={pageloadEvents} searchFilter={props.searchFilter} onUpdatePlayerProgress={(timestampMs: number) => {
            setRecordingProgress(timestampMs);
            sendEmbedMessage({
              recordingProgress: timestampMs
            });
          }} isStitchingEnabled={props.isStitchingEnabled} currentHoveredEvent={props.currentHoveredEvent} setCurrentHoveredEvent={props.setCurrentHoveredEvent} />
            </div>
            <div className={styles.playerControlsArea}>
              <div className={styles.playerControlsAreaLeft}>
                <ButtonReact className={styles.primaryButton} icon={recordingFinished ? "restart" : props.currentRecordingPaused ? "play" : "pause"} onClick={() => toggleRecording()} theme={ButtonTheme.Primary} />
                <SelectReact className={styles.playerControlsAreaSelect} sections={[{
              items: [{
                value: RecordingSpeed.Half,
                label: "0.5x"
              }, {
                value: RecordingSpeed.ThreeQuarters,
                label: "0.75x"
              }, {
                value: RecordingSpeed.Normal,
                label: "1x"
              }, {
                value: RecordingSpeed.Double,
                label: "2x"
              }, {
                value: RecordingSpeed.Quadruple,
                label: "4x"
              }, {
                value: RecordingSpeed.Octuple,
                label: "8x"
              }, {
                value: RecordingSpeed.Dectuple,
                label: "10x"
              }]
            }]} selectedOption={"".concat(recordingSpeed, "x")} onChange={event => updatePlayerSpeed(event.detail.item.value)} menuAlignment={Directions.Up} menuWidth={null} menuHeight={305} />
                {props.showSkipInactiveSwitch && <div className={styles.skipInactivity}>
                    <SwitchReact on={props.isSkipInactivityEnabled} onChange={ev => {
                props.onToggleSkipInactivity(ev.detail.on);
              }} />
                    <span>Skip Inactivity</span>
                  </div>}
              </div>
              <div>{"".concat(formatRecordingProgressTimeBarResult.formattedProgress, " / ").concat(formatRecordingProgressTimeBarResult.formattedDuration)}</div>
              <div className={styles.playerControlsAreaRight}>
                <ButtonReact className={styles.expandButton} icon={props.isFullscreen ? "collapse-diagonal" : "expand"} onClick={() => props.onToggleFullscreen()} tooltip={{
              text: t("session-replay.toggle-fullscreen")
            }} />
                {props.copyButton}
              </div>
            </div>
          </div>}
      </div>
      {hasError ? <ErrorState type={errorState || props.errorState} /> : props.isLoading || loadingRecording ? <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Large} role={"progressbar"} /> : null}
      {props.children}
    </div>;
}